import { Card, CardContent, CardHeader, CardTitle } from '@shared/components/ui/card';
import { Button } from '@shared/components/ui/button';
import { Badge } from '@shared/components/ui/badge';
import { Checkbox } from '@shared/components/ui/checkbox';
import { Label } from '@shared/components/ui/label';
import { Plus, X } from 'lucide-react';

interface InventorySetupStepProps {
  categories: string[];
  assetTypes: string[];
  skipInventory: boolean;
  onCategoriesChange: (value: string[]) => void;
  onAssetTypesChange: (value: string[]) => void;
  onSkipInventoryChange: (value: boolean) => void;
}

export default function InventorySetupStep(props: InventorySetupStepProps) {
  // NO useState, NO useEffect, NO onChange callback
  // Just render inputs with props.value and props.onChange

  const handleAddCategory = () => {
    const newCategory = prompt('Enter category name:');
    if (newCategory && !props.categories.includes(newCategory)) {
      props.onCategoriesChange([...props.categories, newCategory]);
    }
  };

  const handleRemoveCategory = (category: string) => {
    props.onCategoriesChange(props.categories.filter(c => c !== category));
  };

  const handleAddAssetType = () => {
    const newAssetType = prompt('Enter asset type name:');
    if (newAssetType && !props.assetTypes.includes(newAssetType)) {
      props.onAssetTypesChange([...props.assetTypes, newAssetType]);
    }
  };

  const handleRemoveAssetType = (assetType: string) => {
    props.onAssetTypesChange(props.assetTypes.filter(a => a !== assetType));
  };

  return (
    <div className="space-y-6">
      {/* Skip Inventory Option */}
      <Card>
        <CardHeader>
          <CardTitle>Inventory Management</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex items-center space-x-2">
            <Checkbox
              id="skipInventory"
              checked={props.skipInventory}
              onCheckedChange={(checked) => props.onSkipInventoryChange(checked as boolean)}
            />
            <Label htmlFor="skipInventory">
              Skip inventory management for now (can be set up later)
            </Label>
          </div>
        </CardContent>
      </Card>

      {!props.skipInventory && (
        <>
          {/* Asset Categories */}
          <Card>
            <CardHeader>
              <CardTitle>Asset Categories</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex flex-wrap gap-2">
                {props.categories.map((category) => (
                  <Badge key={category} variant="secondary" className="flex items-center gap-1">
                    {category}
                    <button
                      onClick={() => handleRemoveCategory(category)}
                      className="ml-1 hover:text-destructive"
                    >
                      <X className="h-3 w-3" />
                    </button>
                  </Badge>
                ))}
              </div>
              <Button variant="outline" size="sm" onClick={handleAddCategory}>
                <Plus className="h-4 w-4 mr-2" />
                Add Category
              </Button>
            </CardContent>
          </Card>

          {/* Asset Types */}
          <Card>
            <CardHeader>
              <CardTitle>Asset Types</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex flex-wrap gap-2">
                {props.assetTypes.map((assetType) => (
                  <Badge key={assetType} variant="secondary" className="flex items-center gap-1">
                    {assetType}
                    <button
                      onClick={() => handleRemoveAssetType(assetType)}
                      className="ml-1 hover:text-destructive"
                    >
                      <X className="h-3 w-3" />
                    </button>
                  </Badge>
                ))}
              </div>
              <Button variant="outline" size="sm" onClick={handleAddAssetType}>
                <Plus className="h-4 w-4 mr-2" />
                Add Asset Type
              </Button>
            </CardContent>
          </Card>
        </>
      )}
    </div>
  );
}